/**
 * TGB · Judge.me connector
 * ---------------------------------------------------------------------------
 * Single access layer between the storefront and the Judge.me reviews proxy.
 * Every part of the site that needs reviews goes through `window.TGB.judgeme`
 * so the endpoint, the pagination contract and the escaping rules live in one
 * place instead of being re-implemented per section.
 *
 * Configuration is injected by snippets/tgb-judgeme.liquid from theme settings
 * (proxy URL + store UUID). Nothing is hardcoded here and no credential ever
 * reaches the browser: the proxy holds the Judge.me api_token server-side.
 *
 * Proxy contract (see the judgeme_proxy README):
 *   GET {base}/api/reviews/{uuid}[?page&per_page&rating&handle|external_id|product_id]
 *   GET {base}/api/reviews/{uuid}/media[?same params]
 *   - `total` is always null; there is no grand total to show.
 *   - Iterate `page` until `has_more === false` OR `reviews` is empty. The last
 *     exactly-full page reports has_more:true and the next one comes back empty,
 *     so the empty check is required, not belt-and-braces.
 *   - `title` / `body` are UNTRUSTED text. Never inject them as HTML.
 */
(function () {
  'use strict';

  window.TGB = window.TGB || {};
  // Idempotent: the loader snippet may legitimately render more than once.
  if (window.TGB.judgeme) return;

  var config = window.TGB.judgemeConfig || {};
  var BASE_URL = String(config.baseUrl || '').trim().replace(/\/+$/, '');
  var UUID = String(config.uuid || '').trim();

  // Mirrors the proxy's own limits so we fail locally instead of burning a 400.
  var MAX_PAGE = 50;
  var MAX_PER_PAGE = 100;
  var DEFAULT_PER_PAGE = 25;

  function isConfigured() {
    return BASE_URL !== '' && UUID !== '';
  }

  /**
   * Human-readable message per proxy status code. The proxy deliberately
   * returns opaque bodies ({"error": "<code>"}), so the mapping lives here.
   */
  function messageForStatus(status) {
    switch (status) {
      case 400: return 'invalid_request';
      case 404: return 'unknown_scope';
      case 422: return 'unknown_product';
      case 502: return 'upstream_error';
      case 504: return 'upstream_timeout';
      default: return 'request_failed';
    }
  }

  function ConnectorError(code, status) {
    var err = new Error('tgb-judgeme: ' + code);
    err.code = code;
    err.status = status || 0;
    return err;
  }

  /**
   * Build the query string. At most one product selector may be sent — the
   * proxy 400s on more than one, and 400s on repeated params.
   */
  function buildQuery(options) {
    var opts = options || {};
    var params = new URLSearchParams();

    var page = parseInt(opts.page, 10);
    if (!isFinite(page) || page < 1) page = 1;
    if (page > MAX_PAGE) page = MAX_PAGE;
    params.set('page', String(page));

    var perPage = parseInt(opts.perPage, 10);
    if (!isFinite(perPage) || perPage < 1) perPage = DEFAULT_PER_PAGE;
    if (perPage > MAX_PER_PAGE) perPage = MAX_PER_PAGE;
    params.set('per_page', String(perPage));

    var rating = parseInt(opts.rating, 10);
    if (isFinite(rating) && rating >= 1 && rating <= 5) {
      params.set('rating', String(rating));
    }

    // Precedence is explicit so a caller passing two selectors gets a
    // predictable request rather than a 400 from the proxy.
    if (opts.productId) {
      params.set('product_id', String(opts.productId));
    } else if (opts.handle) {
      params.set('handle', String(opts.handle));
    } else if (opts.externalId) {
      params.set('external_id', String(opts.externalId));
    }

    return params.toString();
  }

  function request(pathSuffix, options) {
    if (!isConfigured()) {
      return Promise.reject(ConnectorError('not_configured', 0));
    }
    var url = BASE_URL + '/api/reviews/' + encodeURIComponent(UUID) + pathSuffix +
      '?' + buildQuery(options);

    var init = { method: 'GET', headers: { Accept: 'application/json' } };
    if (options && options.signal) init.signal = options.signal;

    return fetch(url, init).then(function (response) {
      if (!response.ok) {
        throw ConnectorError(messageForStatus(response.status), response.status);
      }
      return response.json();
    });
  }

  /** One page of reviews (each response also carries that page's media[]). */
  function fetchReviews(options) {
    return request('', options);
  }

  /** One page of media only. Note per_page counts REVIEWS scanned, not photos. */
  function fetchMedia(options) {
    return request('/media', options);
  }

  /**
   * Walk every page for a scope and resolve with the flattened list.
   * `onPage(reviews, envelope)` is called per page so a caller can render
   * progressively instead of waiting for the whole walk.
   */
  function fetchAllReviews(options, onPage) {
    var opts = options || {};
    var collected = [];
    var page = 1;

    function step() {
      var pageOptions = Object.assign({}, opts, { page: page });
      return fetchReviews(pageOptions).then(function (envelope) {
        var reviews = (envelope && envelope.reviews) || [];
        collected = collected.concat(reviews);
        if (typeof onPage === 'function') onPage(reviews, envelope);
        // Empty page is the real terminator; has_more can be a false positive
        // when the last page happens to be exactly full.
        if (reviews.length === 0 || !envelope.has_more || page >= MAX_PAGE) {
          return collected;
        }
        page += 1;
        return step();
      });
    }

    return step();
  }

  /** Escape untrusted text before it goes anywhere near innerHTML. */
  function escapeHtml(value) {
    if (value === null || value === undefined) return '';
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  /** Escape, then turn newlines into <br> — review bodies are multi-line. */
  function escapeHtmlWithBreaks(value) {
    return escapeHtml(value).replace(/\r\n|\r|\n/g, '<br>');
  }

  /**
   * Pick the best available picture URL. Any variant may be absent, so the
   * fallback chain is required. URLs are pre-sanitized by the proxy (https +
   * allowlisted host), so they are safe as an <img src>.
   */
  function pictureUrl(urls, preferLarge) {
    if (!urls) return '';
    var order = preferLarge
      ? ['original', 'huge', 'compact', 'small']
      : ['compact', 'small', 'huge', 'original'];
    for (var i = 0; i < order.length; i++) {
      if (urls[order[i]]) return urls[order[i]];
    }
    return '';
  }

  /** "not-yet" is a real value from Judge.me, not an error. */
  function isVerifiedBuyer(review) {
    return !!review && review.verified === 'buyer';
  }

  /**
   * Judge.me stores the literal name "Anonymous" on reviews added through its
   * admin wizard / CSV import without a reviewer name. That is data, not a
   * display choice, so it is treated as "no name" and the caller decides what
   * to print instead (typically a neutral label such as "Collector").
   */
  var ANONYMOUS = /^\s*(anonymous|an[oó]nimo|anonyme|anonym)\s*$/i;

  function isAnonymous(review) {
    var name = review && review.reviewer && review.reviewer.name;
    return !name || ANONYMOUS.test(String(name));
  }

  function reviewerName(review) {
    if (isAnonymous(review)) return '';
    return String(review.reviewer.name).trim();
  }

  window.TGB.judgeme = {
    isConfigured: isConfigured,
    baseUrl: BASE_URL,
    limits: { maxPage: MAX_PAGE, maxPerPage: MAX_PER_PAGE, defaultPerPage: DEFAULT_PER_PAGE },
    fetchReviews: fetchReviews,
    fetchMedia: fetchMedia,
    fetchAllReviews: fetchAllReviews,
    escapeHtml: escapeHtml,
    escapeHtmlWithBreaks: escapeHtmlWithBreaks,
    pictureUrl: pictureUrl,
    isVerifiedBuyer: isVerifiedBuyer,
    isAnonymous: isAnonymous,
    reviewerName: reviewerName
  };
})();
